import Decimal from 'decimal.js-light';

import { CHAIN_IDS } from '@/constants/chain';

import {
  SLIPPAGE_BPS_MULTIPLIER,
  slippageSelector,
  type SlippageMode,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { ClassifierToken } from '@/utils/slippage/classifier';

import { useAutoSlippageBps, useCrossChainAutoSlippageBps } from './useAutoSlippageBps';

interface UseResolvedSlippageParams {
  chainId: number;
  sellToken: ClassifierToken | undefined | null;
  buyToken: ClassifierToken | undefined | null;
  enabled?: boolean;
}

interface UseCrossChainResolvedSlippageParams {
  sellToken: TokenResult | undefined | null;
  buyToken: TokenResult | undefined | null;
  enabled?: boolean;
}

export interface ResolvedSlippage {
  bps: number;
  /**
   * False while an async Auto source is still resolving. Quote builders AND this into
   * their `enabled` so the request is sent once with the settled value (no re-quote /
   * slippage flip). Always true for manual and Solana.
   */
  isReady: boolean;
  /** Whether Auto slippage is active for this trade. */
  isAuto: boolean;
}

/**
 * The stored mode is the single definition of whether Auto governs a market trade.
 * Token-free - the pair only affects the resolved value.
 */
export const isAutoSlippageActive = ({ slippageMode }: { slippageMode: SlippageMode }): boolean =>
  slippageMode === 'auto';

/**
 * The single seam every market builder calls. Manual returns the stored value;
 * Auto returns the pair's category value (score-enriched, resident-signal fallback).
 * The sole drop-in point for a future server-resolved source. Solana uses resident
 * classifier signals while Jupiter applies RTSE independently.
 */
export const useResolvedSlippage = ({
  chainId,
  sellToken,
  buyToken,
  enabled = true,
}: UseResolvedSlippageParams): ResolvedSlippage => {
  const { slippage, slippageMode } = useMarketTradeSettingsStore(slippageSelector);

  const isAuto = isAutoSlippageActive({ slippageMode });

  // Gate the score fetch so manual trades and inactive lanes incur no extra request.
  const { bps: autoBps, isReady } = useAutoSlippageBps({
    chainId,
    sellToken,
    buyToken,
    enabled: enabled && isAuto,
  });

  if (isAuto) {
    return { bps: autoBps, isReady, isAuto: true };
  }

  return {
    bps: new Decimal(slippage).mul(SLIPPAGE_BPS_MULTIPLIER).toInteger().toNumber(),
    isReady: true,
    isAuto: false,
  };
};

/** Resolves the global setting for the cross-chain lane. */
export const useCrossChainResolvedSlippage = ({
  sellToken,
  buyToken,
  enabled = true,
}: UseCrossChainResolvedSlippageParams): ResolvedSlippage => {
  const { slippage, slippageMode } = useMarketTradeSettingsStore(slippageSelector);
  const isAuto = slippageMode === 'auto';

  // Jupiter's RTSE owns market-mode Solana slippage; cross-chain has no equivalent.
  const { bps: autoBps, isReady } = useCrossChainAutoSlippageBps({
    sellToken,
    sellChainId: sellToken?.chainId ?? CHAIN_IDS.MAINNET,
    buyToken,
    buyChainId: buyToken?.chainId ?? CHAIN_IDS.MAINNET,
    enabled: enabled && isAuto,
  });

  if (isAuto) return { bps: autoBps, isReady, isAuto: true };

  return {
    bps: new Decimal(slippage).mul(SLIPPAGE_BPS_MULTIPLIER).toInteger().toNumber(),
    isReady: true,
    isAuto: false,
  };
};
